import { __assign } from "tslib";
/** A submenu to the StandardMenu that _tries_ to be smart about which side,
 *  right or left, that it should render on.
 *
 *  Send it into a group of menu items like this:
 *    {
 *      label: 'do something',
 *      icon: <Icon size={IconSize.SMALL} />,
 *      disabled: isDisabled,
 *      submenu: (
 *        <StandardMenu
 *          ...
 *        />
 *      ),
 *      subMenuPreferredSide: 'left',
 *      estimatedSubmenuWidth: 200,
 *    }
 *
 *   Note: you currently can't use a label or scrollable={true}
 *   on the enclosing menu (or the submenu will appear inside
 *   the scrollable content)
 */
// TODO: the render chain is very snaky and should be simplified at some point
import React, { useRef, useContext, useState, useCallback } from 'react';
import classnames from 'classnames';
import ArrowRightIcon from '@wix/da-ds/pkg/Icons/ArrowRight';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { DropdownStateContext } from '../../../DropdownStateManager';
import StandardMenuStyles from '../StandardMenuOption/StandardMenuOption.scss';
import s from './StandardMenuSubmenu.scss';
/** return how many pixels from the menuitem/open-button to the inner
 *  edges of the browser page */
function checkForRoom(node) {
    var viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    var viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    var bounds = node.getBoundingClientRect();
    return {
        left: bounds.left,
        right: viewportWidth - bounds.right,
        below: viewportHeight - bounds.bottom,
    };
}
/** based on pref props, estimated sizes, and bounds get the best side
 *  to render the submenu on and whether we should render it upwards */
function pickASide(node, preferredSide, estimatedSubmenuWidth, minimumHeightNeeded) {
    var room = checkForRoom(node);
    var horizontal = room.left > room.right ? 'left' : 'right';
    if (preferredSide && room[preferredSide] >= estimatedSubmenuWidth) {
        horizontal = preferredSide;
    }
    return {
        horizontal: horizontal,
        vertical: room.below < minimumHeightNeeded,
    };
}
var StandardMenuSubmenu = function (_a) {
    var className = _a.className, submenu = _a.submenu, disabled = _a.disabled, _b = _a.preferredSide, preferredSide = _b === void 0 ? 'right' : _b, _c = _a.estimatedSubmenuWidth, estimatedSubmenuWidth = _c === void 0 ? 120 : _c, _d = _a.minimumHeightNeeded, minimumHeightNeeded = _d === void 0 ? 80 : _d, children = _a.children;
    var _e = useState(false), submenuIsOpen = _e[0], setSubmenuOpen = _e[1];
    var _f = useState(preferredSide), sideToRenderSubmenu = _f[0], setSideToRenderSubmenu = _f[1];
    var _g = useState(false), renderUpwards = _g[0], setRenderUpwards = _g[1];
    var isMobile = useContext(MobileContext);
    var rootRef = useRef(null);
    var closeDropdown = useContext(DropdownStateContext).closeDropdown;
    var openSubmenu = useCallback(function () {
        if (!rootRef.current) {
            return;
        }
        var _a = pickASide(rootRef.current, preferredSide, estimatedSubmenuWidth, minimumHeightNeeded), horizontal = _a.horizontal, vertical = _a.vertical;
        setSubmenuOpen(true);
        setSideToRenderSubmenu(horizontal);
        setRenderUpwards(vertical);
    }, [estimatedSubmenuWidth, minimumHeightNeeded, preferredSide]);
    var hideSubmenu = useCallback(function () { return setSubmenuOpen(false); }, []);
    var toggleSubmenu = useCallback(function () { return (submenuIsOpen ? hideSubmenu() : openSubmenu()); }, [hideSubmenu, openSubmenu, submenuIsOpen]);
    return (React.createElement("div", __assign({ className: classnames(s['root'], disabled && StandardMenuStyles['disabled'], submenuIsOpen ? s['submenu-open'] : s['submenu-closed'], sideToRenderSubmenu === 'left' && s['submenu-left'], renderUpwards && s['submenu-upwards'], className), ref: rootRef, "aria-expanded": submenuIsOpen, "aria-disabled": disabled }, (isMobile
        ? {
            onClick: toggleSubmenu,
        }
        : {
            onMouseOver: openSubmenu,
            onMouseLeave: hideSubmenu,
            onClick: closeDropdown,
        })),
        children,
        React.createElement(ArrowRightIcon, { className: s['arrow'], size: IconSize.SMALL }),
        React.createElement("div", { className: s['submenu'] }, submenu)));
};
StandardMenuSubmenu.displayName = 'StandardMenuSubmenu';
export default StandardMenuSubmenu;
//# sourceMappingURL=StandardMenuSubmenu.js.map